import { Box, Button, IconButton, Typography } from '@mui/material';
import _ from 'lodash';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import '../../../../assets/style/sass/profile.scss';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { popupActions } from '../../../../redux/slices/onboardingPopupsSlice';
import { fetchUserEducation } from '../../../../redux/slices/user-education/userEducationSlice';
import { disabilityPopupActions } from '../../../../redux/slices/disability/disabilityPopupSlice';
import { fetchUserWorkHistory } from '../../../../redux/slices/user-work/userWorkHistorySlice';
import { getUserDetails } from '../../../../utils/orgName';
import { camelCaseToCapital } from '../../../../utils/topicsMapping';
import { camelCaseToCapitalIndustries } from '../../../../utils/industriesMapping';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import useResponsiveScreenSwitch from '../../../../utils/useResponsiveScreenSwitch';
import { EditIcon } from '../../../../utils/Icons';
import MyChip from '../../components/MyChip';
import RoleTypeTag from '../../components/RoleTypeTag';
import WorkAndEduDesign from '../../components/WorkAndEduDesign';
import VideoPlayer from '../../components/VideoPlayer';
import { GetSocialIcon } from '../../components/SocialIcon';
import { useLoader } from '../../components/useLoader';
import { ProfilePageLoader } from '../../../../Loader/ProfilePageLoader';

interface ProfileCardProps {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}

const ProfileCard = ({ title, children, action }: ProfileCardProps) => (
  <Box className="profileCard">
    <Box className="profileCardHeader">
      <Typography className="profileCardTitle">{title}</Typography>
      <Box className="profileCardHeaderAction">
        {action ?? (
          <div className="profileCardHeaderActionIcon">
            <div className="profileCardHeaderActionIconInner" />
          </div>
        )}
      </Box>
    </Box>
    <Box className="profileCardBody">{children}</Box>
  </Box>
);

const Profile = () => {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.userProfile);
  const data = userDetails.data?.personal_details;
  const userBio = data?.bio;
  const { forMobile, mobileBackBtn } = useResponsiveScreenSwitch();
  const { videoIntro } = useProfileDetails();
  const { userId } = getUserDetails();
  const showLoader = useLoader(userDetails.loading, 1000);
  const workHistoryState = useAppSelector((state) => state.userWorkHistory);
  const educationState = useAppSelector((state) => state.userEducation);
  const [learningObjectives, setLearningObjectives] = useState<string[]>([]);

  const workHistoryData = workHistoryState.updatedData || [];
  const educationData = educationState.updatedData || [];

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserWorkHistory(userId));
      dispatch(fetchUserEducation(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    const objectives = userDetails.data?.learningObjectives?.map((objective) => objective.objective) || [];
    setLearningObjectives([...new Set(objectives)]);
  }, [userDetails.data?.learningObjectives]);

  const topicChips = useMemo(() => {
    const topicLabels = [
      ...camelCaseToCapitalIndustries(userDetails.data?.selectedIndustries || []),
      ...(camelCaseToCapital(userDetails.data?.selectedTopics) || [])
    ];
    return _.sortBy(topicLabels);
  }, [userDetails.data]);

  const handleOpenSocial = () => {
    dispatch(popupActions.updatePopups({ social: true }));
  };

  const handleOpenHobbies = () => {
    dispatch(popupActions.updatePopups({ hobbies: true }));
  };

  const handleOpenDigital = () => {
    if (videoIntro?.includes('blob:')) {
      toast.warning('Upload is in progress');
      return;
    }
    dispatch(popupActions.updatePopups({ digitalIntro: true }));
  };

  const handleAddWorkHistory = () => {
    dispatch(popupActions.updatePopups({ workHistory: { open: true, action: 'Add', item: {} } }));
  };

  const handleAddEducation = () => {
    dispatch(popupActions.updatePopups({ education: { open: true, action: 'Add', item: {} } }));
  };

  const handleEditWorkHistory = (item: any) => {
    dispatch(popupActions.updatePopups({ workHistory: { open: true, action: 'Edit', item } }));
  };

  const handleEditEducation = (item: any) => {
    dispatch(popupActions.updatePopups({ education: { open: true, action: 'Edit', item } }));
  };

  const handleOpenDisability = () => {
    dispatch(disabilityPopupActions.handleDisabilityPopup({ open: true, disable: false }));
  };

  const renderChips = (items: string[], fallback: string) => {
    if (items.length === 0) {
      return <Typography className="profilePlaceholderText">{fallback}</Typography>;
    }
    return (
      <Box className="profileChipRow">
        {items.map((item, index) => (
          <MyChip
            key={`${item}-${index}`}
            label={item}
            color="var(--Text-text-primary, #FDFDFD)"
            backgroundColor="transparent"
            border="1px solid var(--Stroke-Border-border, #444444)"
            fontSize={12}
          />
        ))}
      </Box>
    );
  };

  if (showLoader) {
    return <ProfilePageLoader />;
  }

  if (userDetails.error) {
    return <Box>{userDetails.errorText}</Box>;
  }

  if (!userDetails.data || !data) {
    return null;
  }

  const displayName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Not added yet';
  const initials = (data.firstName || data.lastName || '')
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <>
      {mobileBackBtn('home')}
      <Box className="profilePageLayout">
        <Box className="profileRow">
          <ProfileCard
            title="Personal Information"
            action={
              <IconButton
                className="profileCardActionButton"
                size="small"
                onClick={() => dispatch(popupActions.updatePopups({ profile: { open: true, disable: false } }))}
              >
                <EditIcon />
              </IconButton>
            }
          >
            <Box className="profileInfoSection">
              <Box className="profileAvatarSquare">{initials || 'B'}</Box>
              <Box className="profileInfoDetails">
                <Typography className="profileInfoName">{displayName}</Typography>
                <Typography className="profileBodyText">{data.email || 'Not added yet'}</Typography>
                <Typography className="profileBodyText">
                  {data.phoneNumber ? `${data.phonePrefix} ${data.phoneNumber}` : '-/-'}
                </Typography>
                <Typography className="profileBodyText">
                  {data.country ? `${data.country} (${data.timezone})` : '-/-'}
                </Typography>
                <Box className="profileInfoTags">
                  <RoleTypeTag category={data.category} place="profile" />
                </Box>
              </Box>
            </Box>
          </ProfileCard>

          <ProfileCard title="Bio">
            <Typography className="profileBodyText">{userBio || 'Not added yet'}</Typography>
          </ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard title="My Attributes">{renderChips(learningObjectives, 'Not added yet')}</ProfileCard>
          <ProfileCard title="Topics & Industries">{renderChips(topicChips, 'Not added yet')}</ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard
            title="Work Experience"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleAddWorkHistory}>
                <EditIcon />
              </IconButton>
            }
          >
            <WorkAndEduDesign data={workHistoryData} itemType="work" wherePrint="profile" onEdit={handleEditWorkHistory} />
          </ProfileCard>

          <ProfileCard
            title="Education & Skills"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleAddEducation}>
                <EditIcon />
              </IconButton>
            }
          >
            <WorkAndEduDesign data={educationData} itemType="education" wherePrint="profile" onEdit={handleEditEducation} />
          </ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard
            title="Video Introduction"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenDigital}>
                <EditIcon />
              </IconButton>
            }
          >
            {videoIntro ? (
              <VideoPlayer src={videoIntro} width={forMobile ? '100%' : '360px'} height={forMobile ? '150px' : '220px'} />
            ) : (
              <Box>
                <Typography className="profileBodyText">
                  Video introductions will allow you to introduce yourself to your community and build a network.
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={handleOpenDigital}>
                  Add
                </Button>
              </Box>
            )}
          </ProfileCard>

          <ProfileCard
            title="Social Handles"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenSocial}>
                <EditIcon />
              </IconButton>
            }
          >
            <Box className="profileSocialRow">
              <GetSocialIcon data={data.social || {}} location="profile" />
            </Box>
          </ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard
            title="Disability"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenDisability}>
                <EditIcon />
              </IconButton>
            }
          >
            <Typography className="profileBodyText">
              {data.disability && data.disability.length > 0 ? data.disability.join(', ') : 'No Disabilities'}
            </Typography>
          </ProfileCard>

          <ProfileCard
            title="Hobbies & Interests"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenHobbies}>
                <EditIcon />
              </IconButton>
            }
          >
            {renderChips(data.hobbies || [], 'Not added yet')}
          </ProfileCard>
        </Box>
      </Box>
    </>
  );
};

export default Profile;
